import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import GradientText from "@/components/home/primitives/GradientText";
import SectionHeading from "@/components/home/primitives/SectionHeading";
import { MAGIC_PILLARS } from "@/data/marketing/homepage";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const FeatureCards = () => {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();

  return (
    <section className="w-full bg-white py-16 sm:py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="What Help Genie enables"
          title={
            <>
              What the magic <GradientText>actually does</GradientText>
            </>
          }
          subtitle="One genie. Distributed everywhere. Already plugged into what you run. Trained on what your business actually knows. Live in an hour."
        />

        <div
          ref={ref}
          className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {MAGIC_PILLARS.map((pillar, i) => (
            <div
              key={pillar.id}
              className={cn(
                "rounded-2xl border border-[#E5EAF0] bg-white p-6 md:p-7 shadow-sm transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-xl",
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              )}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-genie-primary/10">
                <GenieIcon name={pillar.genieIcon} size={28} className="h-7 w-7" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-[#222B45]">
                {pillar.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">
                {pillar.description}
              </p>
            </div>
          ))}

          {/* CTA cell completes the 3×2 grid */}
          <div className="flex flex-col justify-center rounded-2xl bg-gradient-to-br from-[#4E9CFF] to-[#FF4DB8] p-6 md:p-7 text-white shadow-lg">
            <h3 className="text-lg font-semibold">See it work for your business</h3>
            <p className="mt-2 text-sm text-white/85">
              Sign up, point it at your documents, and it's answering customers
              within the hour.
            </p>
            <Link
              to="/signup"
              className="mt-5 inline-flex w-fit items-center rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-genie-primary transition hover:bg-white/90"
            >
              Start free
              <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureCards;
